import React, {memo, useState, useLayoutEffect} from 'react';
import {Box, Typography, useMediaQuery} from '@material-ui/core';
import {GuidelineIcon} from '@epic-ui/icons';
import {useTheme} from '@material-ui/core/styles';
import {FilterIcon} from '@epic-ui/icons';
import {getLocale} from 'epic-common';

import IconButton from '../../components/common/IconButton';
import PieChart from '../../components/v2/sections/fortnite-games/components/chart/PieChart';
import {PieChartSkeleton} from '../../components/common/skeleton/PieChartSkeleton';
import {
    PDFPictureTrap,
    PDF_CONTAINER_WIDTH
} from '../../components/v2/sections/fortnite-games/export/PDFExportProvider';
import WidgetDownloadButton from '../../components/v2/sections/fortnite-games/components/WidgetDownloadButton';
import {prepareActivationsExportData} from '../../components/v2/sections/fortnite-games/exportUtils';
import RadioGameSelect from '../../components/radio-game-select/RadioGameSelect';
import {Tooltip} from '../../components/common';
import useLocalization from '../../hooks/useLocalization';
import GameXpDatePicker from '../../components/v2/sections/fortnite-games/game-xp/GameXpDatePicker';
import HighlightsPanel from '../../components/v2/sections/fortnite-games/game-xp/HighlightsPanel';
import {useRangePickerData} from '../../components/common/date-range-picker';
import useGamesData from './useGamesData';
import CalibrationStatus from '../../components/v2/sections/fortnite-games/game-xp/CalibrationStatus';
import ActivationsPanel from '../../components/v2/sections/fortnite-games/game-xp/ActivationsPanel';
import useGameXpData from '../../components/v2/sections/fortnite-games/game-xp/useGameXpData';
import useEmphasisData from '../../components/v2/sections/fortnite-games/game-xp/useEmphasisData';
import {GAME_XP_PRESELECTED_DATE_RANGE} from '../../components/v2/sections/fortnite-games/constants';
import Link from '../../components/common/link/Link';
import {TimeScale, Option} from '../../types';
import {XpCalibrationStatus} from '../../types';

import {OptionWrapper} from '../../components/v2/sections/fortnite-games/styles';
import {
    GameXpWidgetsContainer,
    ActivationsPieChartContainer,
    GameXpSidebarContainer,
    Header,
    DatePickerWrapper,
    GameXpGrid,
    GameXpHeader
} from './FortniteGames.styles';

const ACTIVATION_HEADER_HEIGHT = 110;
const ACTIVATION_ITEM_HEIGHT = 58;

const CODES = {
    title: 'epic.affiliate.fortnite_games.xp',
    tooltip: 'epic.affiliate.fortnite_games.xp.tooltip',
    timeScale: 'epic.affiliate.fortnite_games.time_scale.',
    showMe: 'epic.affiliate.fortnite_games.show_me',
    link: 'epic.affiliate.fortnite_games.xp.calibration_status.learn_more'
};

const calibrationStatusMap = {
    LiveXp: XpCalibrationStatus.SUCCESS,
    Failed: XpCalibrationStatus.ERROR,
    Null: XpCalibrationStatus.DATA_ERROR
};

const GameXp = () => {
    const locale = getLocale();
    const theme = useTheme();
    const isFsmBreakpoint = useMediaQuery(theme.breakpoints.up('fsm'));
    const isFmdBreakpoint = useMediaQuery(theme.breakpoints.up('fmd'));
    const [game, setGame] = useState<Option>(undefined);
    const [activeIndex, setActiveIndex] = useState<number>();
    const [activationIndex, setActivationIndex] = useState<number>();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const getMessage = useLocalization();

    const docsUrl = `https://www.epicgames.com/fortnite/${locale}/creative/docs/games-xp-dashboard-in-fortnite-creative`;
    const index = activeIndex ?? activationIndex;

    const {
        data: {popularGamesOptions},
        loading: gamesLoading
    } = useGamesData();

    const {selectedDate, startDate, endDate, setCalendar} = useRangePickerData(
        GAME_XP_PRESELECTED_DATE_RANGE
    );

    const islandCode = String((game && game.value) || '');

    const {data, total, loading, error} = useGameXpData({islandCode, startDate, endDate});
    const {data: chartData, renderPieEmphasis} = useEmphasisData({data, total, activeIndex: index});

    const status = calibrationStatusMap[data.calibration] || XpCalibrationStatus.WARNING;
    const isLoading = loading || gamesLoading;

    const pieProps = React.useMemo(
        () => ({
            startAngle: 450,
            endAngle: 90,
            activeIndex: index,
            label: renderPieEmphasis,
            onMouseEnter: (_data: any, index: number, _e: React.MouseEvent) => {
                setActiveIndex(index);
            },
            onMouseLeave: () => {
                setActiveIndex(undefined);
            }
        }),
        [index, setActiveIndex, total]
    );

    const activationsListLength = data && data.activations ? data.activations.length : 0;
    const pdfHeight = ACTIVATION_HEADER_HEIGHT + ACTIVATION_ITEM_HEIGHT * activationsListLength;

    // Handlers
    const handleRangeChange = (dateRange: DateRange) => {
        setCalendar(dateRange);
    };

    const handleGameChange = (_event, gameOption) => {
        setGame(gameOption);
    };

    const handleMobileMenuClick = () => {
        setIsMobileMenuOpen(!isMobileMenuOpen);
    };

    useLayoutEffect(() => {
        const selectedGame = popularGamesOptions.slice(0, 1)[0];
        if (selectedGame) {
            setGame(selectedGame);
        }
    }, [popularGamesOptions]);

    const GameSelect = (
        <RadioGameSelect options={popularGamesOptions} value={game} onChange={handleGameChange} />
    );

    const DatePicker = (
        <DatePickerWrapper>
            <GameXpDatePicker
                timeScale={TimeScale.DAILY}
                selectedDate={selectedDate}
                startDate={startDate}
                endDate={endDate}
                onRangeChange={handleRangeChange}
            />
        </DatePickerWrapper>
    );

    const DownloadButton = (
        <WidgetDownloadButton
            areaId={CODES.title}
            fileName={getMessage(CODES.title)}
            pageOptions={{
                orientation: PDF_CONTAINER_WIDTH > pdfHeight ? 'l' : 'p',
                pageFormat: [PDF_CONTAINER_WIDTH, pdfHeight]
            }}
            data={data}
            prepareCSVData={prepareActivationsExportData()}
            disabled={isLoading || !!error}
        />
    );

    return (
        <GameXpWidgetsContainer>
            <GameXpGrid>
                <GameXpHeader>
                    <Header>
                        <Box display="flex" overflow="hidden" minHeight={40} alignItems="center">
                            <Typography variant="h5" color="textPrimary">
                                {getMessage(CODES.title)}
                            </Typography>
                            <Tooltip placement="top" title={getMessage(CODES.tooltip)}>
                                <Box mx={2} display="flex" alignItems="center">
                                    <GuidelineIcon />
                                </Box>
                            </Tooltip>
                            <Link
                                to={docsUrl}
                                isExternalUrl
                                color="textSecondary"
                                style={{
                                    textTransform: 'capitalize',
                                    fontSize: '0.8125rem'
                                }}>
                                {getMessage(CODES.link)}
                            </Link>
                        </Box>
                        {isFmdBreakpoint ? (
                            <>
                                <Box display="flex" justifyContent="space-between" mt={5}>
                                    <Box width={464}>{GameSelect}</Box>
                                    <Box display="flex" alignItems="center">
                                        <Box mx={2} textAlign="right">
                                            <Typography color="textSecondary">
                                                {getMessage(CODES.showMe)}
                                            </Typography>
                                        </Box>
                                        {DatePicker}
                                        {DownloadButton}
                                    </Box>
                                </Box>
                            </>
                        ) : (
                            <Box display="flex">
                                <IconButton
                                    onClick={handleMobileMenuClick}
                                    isOpen={isMobileMenuOpen}>
                                    <FilterIcon />
                                </IconButton>
                                {DownloadButton}
                            </Box>
                        )}
                    </Header>
                    {isMobileMenuOpen && !isFmdBreakpoint && (
                        <Box mt={3}>
                            <OptionWrapper>
                                {GameSelect}
                                {DatePicker}
                            </OptionWrapper>
                        </Box>
                    )}
                    <CalibrationStatus status={status} isLoading={isLoading} />
                </GameXpHeader>
                <GameXpSidebarContainer>
                    <ActivationsPieChartContainer>
                        {isLoading ? (
                            <PieChartSkeleton />
                        ) : (
                            <PieChart data={chartData} pieProps={pieProps} />
                        )}
                    </ActivationsPieChartContainer>
                    {isFsmBreakpoint && (
                        <HighlightsPanel islandCode={islandCode} loading={gamesLoading} />
                    )}
                </GameXpSidebarContainer>
                <PDFPictureTrap id={CODES.title} index={10}>
                    <ActivationsPanel
                        activations={data.activations}
                        isLoading={isLoading}
                        activeIndex={activeIndex}
                        startDate={startDate}
                        endDate={endDate}
                        handleIndexChange={setActivationIndex}
                    />
                </PDFPictureTrap>
                {!isFsmBreakpoint && (
                    <HighlightsPanel islandCode={islandCode} loading={gamesLoading} />
                )}
            </GameXpGrid>
        </GameXpWidgetsContainer>
    );
};

export default memo(GameXp);
